import { markRaw, ref, shallowRef, type Ref } from 'vue';
import type { Id } from '@vp/shared-types';
import { AutoSaver, CanvasDocument, CanvasLock, ComponentSync, History, migrateCoreObject, Recomputer, type CanvasMetaExt, type LockState, type Resolver, type SaveStatus } from '@vp/core';
import { Scene, type DisplayContext, type ModuleRenderProvider } from '@vp/render';
import { availability, logger, metrics, reportStorageError, reportStorageOk, storage } from './platform';
import { t } from './i18n';

export interface SessionHooks {
  modules?: ModuleRenderProvider;
  /** Dependency recompute resolver (module runtime). */
  resolver?: Resolver;
  /** Called with ids of changed objects (module notifications). */
  onObjectsChanged?: (ids: string[]) => void;
  onNotify?: (level: 'info' | 'warning' | 'error', msg: string) => void;
  /** DSkhema display context: module node forms, locale, module type versions. */
  diagramCtx?: () => Pick<DisplayContext, 'diagramShapes' | 'locale' | 'formatVersion' | 'moduleDefaults' | 'moduleText' | 'moduleSimplify'>;
}

/**
 * One open canvas: document, history, scene, autosave and the cross-tab lock (§6.5, §6.6, §10).
 */
export class CanvasSession {
  readonly id: Id;
  readonly doc: CanvasDocument;
  history: History;
  readonly scene: Scene;
  readonly meta: Ref<CanvasMetaExt>;
  readonly saveStatus = ref<SaveStatus>('saved');
  readonly lockState = ref<LockState>('acquiring');
  readonly loadedCount = ref(0);
  readonly loading = ref(true);
  readonly saveError = shallowRef<unknown>(null);
  camera: { cx: number; cy: number; zoom: number } | null = null;
  selection: Id[] = [];
  private saver: AutoSaver | null = null;
  private recomputer: Recomputer | null = null;
  /** Instances follow their component masters (same undo step). */
  private components: ComponentSync;
  private lock: CanvasLock;
  private disposed = false;
  private hooks: SessionHooks;

  private constructor(meta: CanvasMetaExt, hooks: SessionHooks) {
    this.id = meta.id;
    this.meta = ref(meta) as Ref<CanvasMetaExt>;
    this.hooks = hooks;
    this.doc = markRaw(new CanvasDocument(meta.id));
    this.doc.readOnly = true; // until loaded and the lock is ours
    this.history = markRaw(new History(this.doc, availability));
    this.components = markRaw(new ComponentSync(this.doc, this.history));
    this.scene = markRaw(new Scene(this.doc, { unavailableText: t('props.unavailable'), frameLabel: t('props.frameUntitled'), modules: hooks.modules, ...(hooks.diagramCtx?.() ?? {}) }));
    this.camera = meta.camera ?? null;
    this.lock = markRaw(new CanvasLock(meta.id, () => this.flush()));
    this.lock.onChange((s) => {
      this.lockState.value = s;
      if (!this.loading.value) void this.applyLockState(s);
    });
    this.lock.onRemoteSaved(() => void this.refreshFromStorage());
    this.doc.onChange((e) => {
      if (e.updated.length || e.created.length) hooks.onObjectsChanged?.([...e.updated, ...e.created]);
    });
  }

  static async open(id: Id, hooks: SessionHooks, viewport: { w: number; h: number }): Promise<CanvasSession> {
    const meta = await storage.getCanvas(id);
    if (!meta) throw new Error('Canvas not found: ' + id);
    const s = new CanvasSession(meta, hooks);
    await s.lock.acquire();
    s.lockState.value = s.lock.current;
    await s.load(viewport);
    return s;
  }

  private async load(viewport: { w: number; h: number }): Promise<void> {
    const t0 = performance.now();
    const cam = this.camera;
    const view = cam ? { x: cam.cx - viewport.w / 2 / cam.zoom, y: cam.cy - viewport.h / 2 / cam.zoom, w: viewport.w / cam.zoom, h: viewport.h / cam.zoom } : undefined;
    const res = await storage.loadCanvas(this.id, {
      view,
      onChunk: (objs) => {
        // core format migrations keep originals untouched until saved (§8.5, §15.3)
        this.doc.load(objs.map((o) => (o.type.startsWith('core:') ? migrateCoreObject(o) : o)));
        this.loadedCount.value = this.doc.size;
      },
    });
    this.doc.load([], res.links);
    if (res.quarantined) {
      this.hooks.onNotify?.('warning', t('canvas.quarantined', { n: res.quarantined }));
      logger.warn('storage.quarantine', { canvasId: this.id, count: res.quarantined });
    }
    this.loading.value = false;
    metrics.record('canvas.load', performance.now() - t0);
    logger.info('canvas.load', { canvasId: this.id, objects: this.doc.size, ms: Math.round(performance.now() - t0) });
    await this.applyLockState(this.lock.current, res.replay);
  }

  private initialLockApplied = false;

  private async applyLockState(s: LockState, replay: import('@vp/shared-types').Transaction[] = []): Promise<void> {
    if (this.disposed) return;
    const initial = !this.initialLockApplied;
    this.initialLockApplied = true;
    if (s === 'owner') {
      // became the writer after being read-only: the other tab may have saved newer data
      if (!initial && this.doc.readOnly) await this.refreshFromStorage(true);
      this.doc.readOnly = false;
      if (replay.length) {
        for (const tx of replay) {
          try {
            this.doc.applyOps(tx.ops, { source: { kind: 'system' }, label: 'recovery' }, { history: true, replay: true });
          } catch (e) {
            logger.error('storage.replay', { canvasId: this.id, tx: tx.id, error: e instanceof Error ? e.name : 'unknown' });
          }
        }
        this.hooks.onNotify?.('info', t('canvas.recovered', { n: replay.length }));
      }
      if (!this.recomputer && this.hooks.resolver) {
        this.recomputer = markRaw(
          new Recomputer(this.doc, this.history, this.hooks.resolver, {
            stepTimeoutMs: 800,
            onError: (id, e) => logger.warn('recompute.error', { objectId: id, code: (e as { code?: string })?.code ?? 'unknown' }),
          }),
        );
      }
      if (!this.saver) {
        this.saver = markRaw(
          new AutoSaver(this.doc, storage, {
            onStatus: (st, err) => {
              this.saveStatus.value = st;
              this.saveError.value = err ?? null;
              if (st === 'saved') {
                this.lock.announceSaved();
                if (reportStorageOk()) this.hooks.onNotify?.('info', t('storage.recovered'));
              }
              if (st === 'error') {
                const kind = reportStorageError(err);
                logger.error('storage.save', { canvasId: this.id, error: err instanceof Error ? err.name : 'unknown', failure: kind });
              }
            },
            onSaved: (ms) => metrics.record('autosave', ms),
            extraMeta: () => (this.camera ? { camera: this.camera } : {}),
          }),
        );
        if (replay.length) await this.saver.flush();
      }
    } else if (s === 'readonly') {
      this.recomputer?.dispose();
      this.recomputer = null;
      if (this.saver) {
        await this.saver.dispose();
        this.saver = null;
      }
      this.doc.readOnly = true;
    }
  }

  /** Re-read content from storage (read-only tabs follow the writer). */
  async refreshFromStorage(force = false): Promise<void> {
    if (this.disposed || (!force && !this.doc.readOnly)) return;
    const { objects, links } = await storage.readCanvasContent(this.id);
    this.doc.replaceAll(objects, links);
    this.history.clear();
    const meta = await storage.getCanvas(this.id);
    if (meta) this.meta.value = meta;
  }

  async takeOver(): Promise<void> {
    await this.lock.takeOver();
  }

  /** Remember camera for the next open; written with the next autosave batch. */
  setCamera(c: { cx: number; cy: number; zoom: number }): void {
    this.camera = c;
    this.saver?.touchMeta();
  }

  async flush(): Promise<void> {
    await this.saver?.flush();
  }

  get isReadOnly(): boolean {
    return this.doc.readOnly;
  }

  /**
   * Parts of the canvas written outside transactions (canvas-parts.ts: modules' data, data sets later)
   * follow the same rule as objects: only the tab that owns the canvas writes them.
   */
  get canWriteParts(): boolean {
    return !this.doc.readOnly && this.lock.current === 'owner';
  }

  /** After writing a part: the other tabs of this canvas re-read it. */
  partChanged(part: string, key?: string): void {
    this.lock.announcePartChanged(part, key);
  }

  /** A part of this canvas changed in another tab. */
  onPartChanged(fn: (part: string, key?: string) => void): () => void {
    return this.lock.onRemotePartChanged(fn);
  }

  async dispose(): Promise<void> {
    if (this.disposed) return;
    await this.saver?.dispose();
    this.disposed = true;
    this.saver = null;
    this.components.dispose();
    this.recomputer?.dispose();
    this.lock.dispose();
    this.scene.dispose();
    this.history.dispose();
  }
}
